import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Users, Search, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Mail, Phone, X, Copy, Check } from 'lucide-react';
import { useGuests, type GuestRow } from '../../../hooks/useManagement';

type SortKey = 'name' | 'country' | 'bookings_count' | 'total_nights' | 'total_spent' | 'last_stay';
type FilterKey = 'all' | 'repeat' | 'upcoming' | 'registered' | 'with_email';

const eur = (n: number | null) => `€${(n ?? 0).toLocaleString('es-ES')}`;

export default function ManageGuestsPage() {
  const { t } = useTranslation();
  const { data: guests, isLoading } = useGuests();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortKey, setSortKey] = useState<SortKey>('last_stay');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<GuestRow | null>(null);
  const [copied, setCopied] = useState(false);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'name' || k === 'country' ? 'asc' : 'desc'); }
  };

  const all = guests ?? [];
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = all;
    if (filter === 'repeat') rows = rows.filter(g => (g.stays_count ?? 0) >= 2);
    else if (filter === 'upcoming') rows = rows.filter(g => g.upcoming);
    else if (filter === 'registered') rows = rows.filter(g => g.has_registration);
    else if (filter === 'with_email') rows = rows.filter(g => !!g.email);
    if (term) {
      rows = rows.filter(g =>
        (g.name ?? '').toLowerCase().includes(term) ||
        (g.email ?? '').toLowerCase().includes(term) ||
        (g.phone ?? '').toLowerCase().includes(term) ||
        (g.country ?? '').toLowerCase().includes(term) ||
        (g.properties ?? '').toLowerCase().includes(term) ||
        (g.doc_number ?? '').toLowerCase().includes(term));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'es') * dir;
    });
  }, [all, search, filter, sortKey, sortDir]);

  const filters: { key: FilterKey; label: string }[] = [
    { key: 'all', label: t('portal.manage.guests.filterAll', 'Todos') },
    { key: 'repeat', label: t('portal.manage.guests.filterRepeat', 'Recurrentes') },
    { key: 'upcoming', label: t('portal.manage.guests.filterUpcoming', 'Próxima estancia') },
    { key: 'registered', label: t('portal.manage.guests.filterRegistered', 'Check-in completado') },
    { key: 'with_email', label: t('portal.manage.guests.filterWithEmail', 'Con email') },
  ];

  const SortTh = ({ k, children, right }: { k: SortKey; children: React.ReactNode; right?: boolean }) => (
    <th className={`px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider ${right ? 'text-right' : 'text-left'}`}>
      <button onClick={() => toggleSort(k)} className={`inline-flex items-center gap-1 hover:text-slate-800 ${right ? 'flex-row-reverse' : ''}`}>
        {children}
        {sortKey === k ? (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
      </button>
    </th>
  );

  const copyEmails = async () => {
    const emails = filtered.map(g => g.email).filter(Boolean).join(', ');
    try { await navigator.clipboard.writeText(emails); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* */ }
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users size={22} className="text-teal-600" /> {t('portal.manage.guests.title', 'Huéspedes')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.guests.subtitle', 'Todos tus huéspedes con su historial de estancias, contacto y registro.', { count: all.length })}</p>
        </div>
        <button onClick={copyEmails} disabled={!filtered.some(g => g.email)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
          {t('portal.manage.guests.copyEmails', 'Copiar emails')}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Filters */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {filters.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${filter === f.key ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* Search */}
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('portal.manage.guests.searchPlaceholder', 'Buscar por nombre, email, teléfono, país…')}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">{t('portal.manage.guests.empty', 'No hay huéspedes que coincidan.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <SortTh k="name">{t('portal.manage.guests.colGuest', 'Huésped')}</SortTh>
                  <SortTh k="country">{t('portal.manage.guests.colCountry', 'País')}</SortTh>
                  <SortTh k="bookings_count" right>{t('portal.manage.guests.colBookings', 'Reservas')}</SortTh>
                  <SortTh k="total_nights" right>{t('portal.manage.guests.colNights', 'Noches')}</SortTh>
                  <SortTh k="total_spent" right>{t('portal.manage.guests.colSpent', 'Gastado')}</SortTh>
                  <SortTh k="last_stay">{t('portal.manage.guests.colLastStay', 'Última estancia')}</SortTh>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-left">{t('portal.manage.guests.colStatus', 'Estado')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(g => (
                  <tr key={g.guest_key} onClick={() => setSelected(g)} className="hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{g.name ?? '—'}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-2">
                        {g.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{g.email}</span>}
                        {g.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{g.phone}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{g.country ?? g.nationality ?? '—'}</td>
                    <td className="px-5 py-3 text-right text-slate-700">{g.bookings_count}</td>
                    <td className="px-5 py-3 text-right text-slate-700">{g.total_nights ?? 0}</td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900">{eur(g.total_spent)}</td>
                    <td className="px-5 py-3 text-slate-600">{g.last_stay ? format(parseISO(g.last_stay), 'd MMM yyyy', { locale: es }) : '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {!!g.upcoming && <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border bg-teal-50 text-teal-700 border-teal-200">{t('portal.manage.guests.badgeUpcoming', 'Próxima')}</span>}
                        {(g.stays_count ?? 0) >= 2 && <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border bg-violet-50 text-violet-700 border-violet-200">{t('portal.manage.guests.badgeRepeat', 'Recurrente')}</span>}
                        {!!g.has_registration && <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">{t('portal.manage.guests.badgeRegistered', 'Check-in ✓')}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Guest detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 text-lg">{selected.name ?? '—'}</h3>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <DetailRow k={t('portal.manage.guests.fieldEmail', 'Email')} v={selected.email ?? '—'} />
              <DetailRow k={t('portal.manage.guests.fieldPhone', 'Teléfono')} v={selected.phone ?? '—'} />
              <DetailRow k={t('portal.manage.guests.colCountry', 'País')} v={selected.country ?? '—'} />
              <DetailRow k={t('portal.manage.guests.fieldNationality', 'Nacionalidad')} v={selected.nationality ?? '—'} />
              <DetailRow k={t('portal.manage.guests.fieldDoc', 'Documento')} v={selected.doc_number ?? '—'} />
              <DetailRow k={t('portal.manage.guests.fieldLanguage', 'Idioma')} v={selected.language ?? '—'} />
              <DetailRow k={t('portal.manage.guests.colBookings', 'Reservas')} v={String(selected.bookings_count)} />
              <DetailRow k={t('portal.manage.guests.colNights', 'Noches')} v={String(selected.total_nights ?? 0)} />
              <DetailRow k={t('portal.manage.guests.colSpent', 'Gastado')} v={eur(selected.total_spent)} />
              <DetailRow k={t('portal.manage.guests.fieldFirstStay', 'Primera estancia')} v={selected.first_stay ? format(parseISO(selected.first_stay), 'd MMM yyyy', { locale: es }) : '—'} />
              <DetailRow k={t('portal.manage.guests.colLastStay', 'Última estancia')} v={selected.last_stay ? format(parseISO(selected.last_stay), 'd MMM yyyy', { locale: es }) : '—'} />
              <DetailRow k={t('portal.manage.guests.fieldProperties', 'Alojamientos')} v={selected.properties ?? '—'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 font-medium text-right break-all">{v}</span>
    </div>
  );
}
